<template>
    <AppLoadingContainer :predicate="isLoading">
        <p>
            Delegates are other employees for which you have allowed to act in your place for a given role, and may perform all of the actions for that role for which you have access.
        </p>
        <div class="accordion" role="tablist">
            <div v-if="roles.length === 0" class="alert alert-secondary text-center">
                <span>You do not have a role that allows delegates</span>
            </div>
            <template v-for="(role, roleIndex) in roles">
                <b-card no-body class="mb-1" :key="roleIndex">
                    <b-card-header header-tag="header" class="p-3 bg-secondary text-white" role="tab" v-b-toggle="role.name + '-section'" variant="info" 
                        @click="activeRoleSection === roleIndex ? activeRoleSection = null : activeRoleSection = roleIndex;">
                        <i :class="activeRoleSection == roleIndex ? 'fas fa-caret-up' : 'fas fa-caret-down'"></i> {{ role.name }}
                    </b-card-header>
                    <b-collapse :id="role.name + '-section'" :visible="roleIndex === 0 ? true : null" accordion="my-accordion" role="tabpanel">
                        <b-card-body>
                            <div class="alert alert-info">
                                Will grant access to the following sections: 
                                <ul>
                                    <li v-for="section in role.applicationSections" :key="section">{{ section }}</li>
                                </ul>
                            </div>
                            <div class="mb-2 text-right">
                                <button @click="addingDelegateRoleId=role.id;addingDelegateRole=role.name;isAddModalVisible=true;" 
                                        :disabled="isSubmitting"
                                        class="btn btn-primary d-print-none" 
                                        data-toggle="modal">
                                        <i class="fa fa-plus"></i>Add
                                </button>
                            </div>
                            <BTable :busy="isLoading"
                                    :fields="tableFields"
                                    :items="delegates.filter(delegate => { return delegate.delegateRoleId == role.id })"
                                    :responsive="true"
                                    primary-key="id"
                                    show-empty
                                    striped
                                    table-class="table-align-middle">

                                <template v-slot:cell(delegatePersonId)="row">
                                    <b-button :disabled="isSubmitting" 
                                                @click="deletingDelegatePersonId=row.item.delegatePersonId; deletingDelegateName=row.item.delegateName; deletingDelegateRoleId=role.id; deletingDelegateRole=role.name; isDeleteModalVisible=true;" 
                                                size="sm"
                                                variant="danger">
                                        <i class="fa fa-trash"></i>Delete
                                    </b-button>
                                </template>

                                <template v-slot:cell(createdDate)="row">
                                    <span>{{ row.item.createdDate | moment('MM/DD/YYYY') }}</span>
                                </template>
                            </BTable>
                        </b-card-body>
                    </b-collapse>
                </b-card>   
            </template>
        </div>
        <AppModal @close="onAddClose()" v-bind="$attrs" v-if="isAddModalVisible">
            <template v-slot:header>Add {{ addingDelegateRole }} Delegate</template>
            <form @submit.prevent="onAddSubmit()">
                <div class="form-group">
                    <label for="delegatePersonId">Employee</label>
                    <select class="form-control"
                            v-model="addingDelegatePersonId">
                        <option :value="null" disabled>--Select Employee--</option>
                        <option :key="employee" :value="employee.id"
                                v-for="employee in allTTIPeople">{{employee.display}}
                        </option>
                    </select>
                </div>
            </form>
            <template v-slot:footer>
                <button :disabled="isSubmitting" @click.prevent="onAddSubmit()" class="btn btn-primary" type="submit">Save
                </button>
                <button :disabled="isSubmitting" @click="onAddClose()" class="btn btn-danger" data-dismiss="modal"
                        type="button">
                    Close
                </button>
            </template>
        </AppModal>
        <AppModalConfirm :visible="isDeleteModalVisible" @close="onDeleteClose()" @confirm="onDeleteConfirmed()">
            <template v-slot:header>Confirm</template>
            <p>Are you sure you want to delete {{deletingDelegateName}} as a delegate for the role {{deletingDelegateRole}}?</p>
        </AppModalConfirm>
    </AppLoadingContainer>
</template>

<script>
    import PersonService from "@/services/PersonService";
    import AppModal from "@/components/shared/AppModal.vue"
    import AppModalConfirm from "@/components/shared/AppModalConfirm.vue"
    import AppLoadingContainer from "@/components/shared/AppLoadingContainer";
    import {ToastType} from "@/services/models/shared/ToastType";

    export default {
        components: {
            AppModal,
            AppModalConfirm,
            AppLoadingContainer
        },
        data() {
            return {
                isLoading: false,
                isSubmitting: false,
                allTTIPeople: [],
                delegates: [],
                roles: [],
                activeRoleSection: 0,
                tableFields: [
                    {
                        key: 'delegatePersonId',
                        label: '',
                        thStyle: 'width:200px'
                    },
                    {
                        key: 'delegateName',
                        label: 'Name'
                    },
                    {
                        key: 'createdDate',
                        label: "Date Added"
                    }
                ],
                isAddModalVisible: false,
                addingDelegateRoleId: null,
                addingDelegateRole: null,
                addingDelegatePersonId: null,
                isDeleteModalVisible: false,
                deletingDelegatePersonId: null,
                deletingDelegateName: null,
                deletingDelegateRoleId: null,
                deletingDelegateRole: null
            }
        },
        methods: {
            loadData() {
                const vm = this;
                vm.isLoading = true;

                Promise.all([
                    PersonService.getPersonDelegates(this.$store.getters.user.id)
                        .then(response => vm.delegates = response),

                    PersonService.getPeopleList({
                        onlyShowActive: true,
                        onlyShowTTI: false,
                        onlyShowMissingAddress: false,
                        employeeClassification : 2,
                        adStatus: 1,
                        returnAll: true
                    })
                        .then(response => vm.allTTIPeople = response.data.map(item => { return { id: item.id, display: `${item.name} (${item.organizationCode})` } })),

                    PersonService.getRolesForPerson(this.$store.getters.user.id, {
                        includeUnassignableRoles: true,
                        onlyDelegatableRoles: true
                    })
                        .then(response => vm.roles = response)]).catch(() => {
                    vm.redirectToError();
                }).finally(() => {
                    vm.isLoading = false;
                });
            },

            onAddSubmit() {
                const vm = this;
                vm.isSubmitting = true;

                PersonService.addPersonDelegate(vm.$store.getters.user.id, {
                    delegatePersonId: vm.addingDelegatePersonId,
                    delegateRoleId: vm.addingDelegateRoleId
                }).then(() => {
                    vm.alert(ToastType.Success, "The delegate was successfully created.");
                }).catch(function (error) {
                    vm.alert(ToastType.Danger, "Failed to add the delegate.");
                }).finally(() => {
                    vm.onAddClose();
                    vm.isSubmitting = false;
                    this.loadData();
                });
            },

            onDeleteConfirmed() {
                const vm = this;
                vm.isSubmitting = true;

                PersonService.deletePersonDelegate(vm.$store.getters.user.id, {
                    delegatePersonId: vm.deletingDelegatePersonId,
                    delegateRoleId: vm.deletingDelegateRoleId
                }).then(() => {
                    vm.alert(ToastType.Success, "The delegate was successfully removed.");
                }).catch(function (error) {
                    vm.alert(ToastType.Danger, "Failed to remove the delegate.");
                }).finally(() => {
                    vm.onDeleteClose();
                    vm.isSubmitting = false;
                    this.loadData();
                });
            },

            onAddClose() {
                this.isAddModalVisible = false;
                this.addingDelegateRoleId = null;
                this.addingDelegateRole = null;
                this.addingDelegatePersonId = null;
            },

            onDeleteClose() {
                this.isDeleteModalVisible = false;
                this.deletingDelegateRoleId = null;
                this.deletingDelegateRole = null;
                this.deletingDelegatePersonId = null;
                this.deletingDelegateName = null;
            }
        },
        created () {
            this.loadData();
        }
    }
</script>